import { useEffect, useEffectEvent } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { FAIRDEE_CONTACT_NUMBER, FAIRDEE_CONTACT_NUMBER_LABEL, FAIRDEE_FB_URL } from '../../constants';
import LogoPlus from '../../images/common/logo-plus.svg';
import ThailandFlag from '../../images/icons/thailand-flag.svg';
import UkFlag from '../../images/icons/uk-flag.svg';
import LineIcon from '../../images/latest-landing/line.png';
import { getLineUrl, getParameterByName, isMobileDevice } from '../../utils/helper';
import { trackEvent } from '@/modules/shared/analytics';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { useAppStore, type LocaleCode } from '@/modules/shared/store/appStore';
import { en as commonEn, th as commonTh } from '@/modules/shared/i18n/locales';

import { Facebook, Phone, Sun, Moon, Maximize, Minimize } from 'lucide-react';

import { useTheme } from '../../providers/theme-provider';
import { Button } from '../ui/button';
import { useFullscreen } from '@/modules/shared/hooks/useFullscreen';

type Language = 'english' | 'thai';

interface LatestHeaderProps {
  isIg?: boolean;
  isDirectAgent?: boolean;
  isBusinessManager?: boolean;
}

/**
 * LatestHeader component — simplified for admin portal.
 * Shows logo, social links, login/signup, and language toggle.
 */
function LatestHeader({ isIg }: LatestHeaderProps) {
  const language = useAppStore((s) => s.language);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { isFullscreen, isSupported: isFullscreenSupported, toggle: toggleFullscreen } = useFullscreen();

  // Header translations (Zustand + shared common locale)
  const { t } = useTranslation({ en: commonEn, th: commonTh });

  const onLoginIntent = useEffectEvent(login);
  useEffect(() => {
    if (getParameterByName('intent') === 'login') {
      onLoginIntent();
    }
  }, []);

  function login() {
    trackEvent('login_signup_clicked', { parent: 'Landing Header' });
    navigate('/');
  }

  // `isFullscreen` here is the pre-toggle state, so it describes what the click is asking for.
  function handleFullscreenClick() {
    trackEvent('fullscreen_clicked', {
      parent: 'Landing Header',
      action: isFullscreen ? 'exit' : 'enter',
    });
    void toggleFullscreen();
  }

  // Language state lives in the Zustand appStore (LocaleCode 'en' | 'th').
  function toggleLanguage(selectedLanguage: Language) {
    const localeCode: LocaleCode = selectedLanguage === 'english' ? 'en' : 'th';
    useAppStore.getState().setLanguage(localeCode);
    localStorage.setItem('language', selectedLanguage);
  }

  return (
    <div className="bg-card shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-3">
          <div>
            <NavLink to="/">
              <img src={LogoPlus} alt="FairDee" className="h-8" />
            </NavLink>
          </div>

          <div className="flex items-center gap-4">
            {!isMobileDevice() && (
              <a href={`tel:${FAIRDEE_CONTACT_NUMBER}`} className="flex items-center gap-2 text-foreground">
                <Phone size={16} />
                <span>{FAIRDEE_CONTACT_NUMBER_LABEL}</span>
              </a>
            )}

            <div className="flex items-center gap-3">
              <a
                href={FAIRDEE_FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-royal-blue-500 hover:text-royal-blue-600"
              >
                <Facebook size={isMobileDevice() ? 20 : 16} />
              </a>
              <a href={getLineUrl()} target="_blank" rel="noopener noreferrer">
                <img src={LineIcon} alt="Line" className="h-5 w-5" />
              </a>
            </div>

            {!isIg && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={login}>
                  {t('login')}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2 ml-2">
              <button
                className={`flex items-center gap-1 px-2 py-1 rounded ${theme === 'light' ? 'bg-accent' : ''}`}
                onClick={() => setTheme('light')}
                aria-label="Light mode"
              >
                <Sun className="h-4 w-4" />
              </button>
              <button
                className={`flex items-center gap-1 px-2 py-1 rounded ${theme === 'dark' ? 'bg-accent' : ''}`}
                onClick={() => setTheme('dark')}
                aria-label="Dark mode"
              >
                <Moon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-2 ml-2">
              <button
                className={`flex items-center gap-1 px-2 py-1 rounded ${language === 'th' ? 'bg-accent' : ''}`}
                onClick={() => toggleLanguage('thai')}
              >
                <img src={ThailandFlag} alt="Thai" className="h-4 w-5" />
                <span className="text-sm">ไทย</span>
              </button>
              <button
                className={`flex items-center gap-1 px-2 py-1 rounded ${language === 'en' ? 'bg-accent' : ''}`}
                onClick={() => toggleLanguage('english')}
              >
                <img src={UkFlag} alt="English" className="h-4 w-5" />
                <span className="text-sm">EN</span>
              </button>
            </div>

            {isFullscreenSupported && (
              <div className="flex items-center gap-2 ml-2">
                <button
                  className="flex items-center gap-1 px-2 py-1 rounded"
                  onClick={handleFullscreenClick}
                  aria-pressed={isFullscreen}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LatestHeader;
